import { Box, Button, Col, FormGroup, Icon, IconButton, Row, Select, Tooltip } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { IOption } from '@frontend/components/interfaces';
import { translateKey } from '@frontend/translate';
import { getValidationErrorTranslation } from '@frontend/utils';

interface IDeviceStatusOrganizationsSelectionProps {
    className?: string;
    hasErrors?: boolean;
    onChange: (selectedDeviceStatusOrganizationIds: string[]) => void;
    onChangeValidationError: (error?: string) => void;
    organizationOptionsList: IOption<string>[];
    selectedDeviceStatusOrganizationIds: string[];
}

export function DeviceStatusOrganizationsSelection(props: IDeviceStatusOrganizationsSelectionProps) {
    const [areAllOrganizationFieldsFilled, setAreAllOrganizationFieldsFilled] = useState<boolean>(
        !!props.selectedDeviceStatusOrganizationIds
    );
    const [areAllEligibleOrganizationsSelected, setAreAllEligibleOrganizationsSelected] = useState<boolean>(false);
    const [selectedDeviceStatusOrganizationIds, setSelectedDeviceStatusOrganizationIds] = useState<string[]>(
        props.selectedDeviceStatusOrganizationIds
    );

    useEffect(() => {
        if (props.selectedDeviceStatusOrganizationIds.length === 0) {
            initializeDeviceStatusOrganizationsSelectionFields();
        }
    }, [props.selectedDeviceStatusOrganizationIds]);

    useEffect(() => {
        if (props.organizationOptionsList.length > 0) {
            setAreAllEligibleOrganizationsSelected(
                selectedDeviceStatusOrganizationIds.length === props.organizationOptionsList.length
            );
        }
        props.onChange(selectedDeviceStatusOrganizationIds);
    }, [selectedDeviceStatusOrganizationIds]);

    useEffect(() => {
        validateDeviceStatusOrganizationsFields();
    }, [areAllOrganizationFieldsFilled, areAllEligibleOrganizationsSelected]);

    return (
        <>
            {selectedDeviceStatusOrganizationIds.map((organizationId, key) => {
                return (
                    <FormGroup
                        isRequired={true}
                        key={key}
                        label={translateKey('organization.organization')}
                    >
                        <Box
                            alignItems="center"
                            display="flex"
                            gap={2}
                        >
                            <Select
                                className={props.className}
                                onChange={(option) => updateDeviceOrganization(key, option!.value)}
                                options={loadUnselectedOrganizations(key)}
                                placeholder={translateKey('devices.organizationPlaceholder')}
                                value={getOrganizationSelectedValue(organizationId)}
                            />
                            {(selectedDeviceStatusOrganizationIds.length !== 1 || !areAllOrganizationFieldsFilled) && (
                                <IconButton
                                    icon="delete"
                                    onClick={() => removeDeviceOrganization(key)}
                                />
                            )}
                        </Box>
                    </FormGroup>
                );
            })}

            {!areAllEligibleOrganizationsSelected && !areAllOrganizationFieldsFilled && (
                <FormGroup
                    hasErrors={props.hasErrors}
                    isRequired={true}
                    key={selectedDeviceStatusOrganizationIds.length}
                    label={translateKey('organization.organization')}
                >
                    <Box
                        display="flex"
                        gap={2}
                    >
                        <Box width="100%">
                            <Select
                                className={props.className}
                                error={props.hasErrors ? getValidationErrorTranslation('any_selected') : undefined}
                                onChange={(option) => {
                                    addDeviceOrganization(option!.value);
                                    setAreAllOrganizationFieldsFilled(true);
                                }}
                                options={loadUnselectedOrganizations()}
                                placeholder={translateKey('devices.organizationPlaceholder')}
                            />
                        </Box>
                        {!!selectedDeviceStatusOrganizationIds.length && (
                            <IconButton
                                icon="delete"
                                onClick={() => setAreAllOrganizationFieldsFilled(true)}
                            />
                        )}
                    </Box>
                </FormGroup>
            )}
            {!areAllEligibleOrganizationsSelected && areAllOrganizationFieldsFilled && (
                <Row
                    columnGap={[2, 4]}
                    rowGap={[2, 4]}
                >
                    <Col span={[12, null, 4]}></Col>
                    <Col span={[12, null, 8]}>
                        <Button
                            iconLeft="add"
                            onClick={() => setAreAllOrganizationFieldsFilled(false)}
                            variant="link"
                        >
                            {translateKey('organization.add')}
                            <Tooltip
                                content={translateKey('devices.addOrganizationTooltip')}
                                placement="right"
                            >
                                <Icon
                                    color="primary"
                                    icon="information"
                                    sx={{ marginLeft: '8px', marginTop: '4px' }}
                                />
                            </Tooltip>
                        </Button>
                    </Col>
                </Row>
            )}
        </>
    );

    function validateDeviceStatusOrganizationsFields() {
        if (!areAllOrganizationFieldsFilled && !areAllEligibleOrganizationsSelected) {
            props.onChangeValidationError('any_selected');
        } else {
            props.onChangeValidationError();
        }
    }

    function initializeDeviceStatusOrganizationsSelectionFields() {
        setAreAllEligibleOrganizationsSelected(false);
        setAreAllOrganizationFieldsFilled(false);
        setSelectedDeviceStatusOrganizationIds([]);
    }

    function removeDeviceOrganization(index: number) {
        const updatedDeviceOrganizations = [...selectedDeviceStatusOrganizationIds];
        updatedDeviceOrganizations.splice(index, 1);
        setSelectedDeviceStatusOrganizationIds(updatedDeviceOrganizations);

        if (areAllEligibleOrganizationsSelected) {
            setAreAllOrganizationFieldsFilled(true);
        }
    }

    function updateDeviceOrganization(index: number, organizationId: string) {
        const updatedDeviceOrganizations = [...selectedDeviceStatusOrganizationIds];
        updatedDeviceOrganizations[index] = organizationId;
        setSelectedDeviceStatusOrganizationIds(updatedDeviceOrganizations);
    }

    function addDeviceOrganization(organizationId: string) {
        setSelectedDeviceStatusOrganizationIds([...selectedDeviceStatusOrganizationIds, organizationId]);
    }

    function getOrganizationSelectedValue(organizationId: string) {
        return props.organizationOptionsList.find((item) => item.value === organizationId);
    }

    function loadUnselectedOrganizations(organizationIndex?: number) {
        return props.organizationOptionsList.filter(
            (organizationOption) =>
                !selectedDeviceStatusOrganizationIds.includes(organizationOption.value) ||
                (organizationIndex !== undefined &&
                    selectedDeviceStatusOrganizationIds[organizationIndex] === organizationOption.value)
        );
    }
}
